import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../services/api";
import { getMyPurchases } from "../services/purchase";
import {
  ClipboardCheck,
  Clock,
  BookOpen,
  ArrowRight,
  ArrowLeft,
  Search,
  Filter
} from "lucide-react";
import { motion } from "framer-motion";
import toast, { Toaster } from 'react-hot-toast';
import PublicNavbar from "../components/PublicNavbar";

export default function PublicExams() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasedIds, setPurchasedIds] = useState([]);

  useEffect(() => {
    if (localStorage.getItem("token")) {
      getMyPurchases().then(res => setPurchasedIds(res.data.map(c => String(c.id)))).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const fetchExams = async () => {
      try {
        const res = await api.get("/exams/public/list");
        setExams(res.data);
      } catch (err) {
        console.error("Error fetching exams:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchExams();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-['Inter',sans-serif]">
      <Toaster position="top-center" />

      <PublicNavbar />

      <main className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
          <div>
            <h2 className="text-3xl font-black text-slate-900 mb-2">Crack Your Exams</h2>
            <p className="text-slate-500 font-medium">Practice with full-length mock tests designed by experts</p>
          </div>

          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search tests..."
                className="pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-600/10 transition-all w-64"
              />
            </div>
            <button className="p-3 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-indigo-600">
              <Filter size={20} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {exams.map((exam, i) => (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                key={i}
                className="group bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all relative overflow-hidden"
              >
                <div className="p-3 bg-slate-50 rounded-xl w-fit mb-6 text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                  <ClipboardCheck size={24} />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-4 line-clamp-2 min-h-[3.5rem] group-hover:text-indigo-600 transition-colors">
                  {exam.title}
                </h3>
                <div className="flex items-center gap-4 text-xs font-black text-slate-400 mb-8 uppercase tracking-widest">
                  <span className="flex items-center gap-1.5"><BookOpen size={14} /> {exam.questions?.length || 0} Qs</span>
                  <span className="flex items-center gap-1.5"><Clock size={14} /> {exam.duration_minutes} Mins</span>
                </div>
                <button
                  onClick={() => {
                    if (localStorage.getItem("token")) {
                      navigate("/exams");
                    } else {
                      navigate("/login");
                    }
                  }}
                  className="w-full py-4 bg-slate-900 text-white font-black rounded-2xl shadow-xl shadow-slate-200 group-hover:bg-indigo-600 transition-all active:scale-[0.98]"
                >
                  {purchasedIds.includes(String(exam.course_id)) ? "Attempt Test" : "Enroll Now"}
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
